import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuditLogs } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ClipboardList, User, Box, MapPin, Tag, Calendar, X } from 'lucide-react';
import { formatDateTime } from '../lib/utils';

const actionColors = {
  USER_CREATED: 'bg-green-100 text-green-800',
  USER_UPDATED: 'bg-blue-100 text-blue-800',
  USER_DELETED: 'bg-red-100 text-red-800',
  ROLE_ASSIGNED: 'bg-purple-100 text-purple-800',
  ROLE_REMOVED: 'bg-orange-100 text-orange-800',
  ASSET_CREATED: 'bg-green-100 text-green-800',
  ASSET_UPDATED: 'bg-blue-100 text-blue-800',
  ASSET_TRANSFERRED: 'bg-indigo-100 text-indigo-800',
  ASSET_RECEIVED: 'bg-teal-100 text-teal-800',
  ASSET_SENT_REPAIR: 'bg-amber-100 text-amber-800',
  ASSET_TRASHED: 'bg-red-100 text-red-800',
  ASSET_REMOVED: 'bg-red-100 text-red-800',
  REMOVAL_REQUESTED: 'bg-orange-100 text-orange-800',
  REMOVAL_APPROVED: 'bg-green-100 text-green-800',
  REMOVAL_DENIED: 'bg-red-100 text-red-800',
  EDIT_REQUESTED: 'bg-yellow-100 text-yellow-800',
  EDIT_APPROVED: 'bg-green-100 text-green-800',
  EDIT_DENIED: 'bg-red-100 text-red-800',
  LOGIN: 'bg-gray-100 text-gray-800',
  LOCATION_CREATED: 'bg-green-100 text-green-800',
  LOCATION_UPDATED: 'bg-blue-100 text-blue-800',
  CATEGORY_CREATED: 'bg-green-100 text-green-800',
  CATEGORY_UPDATED: 'bg-blue-100 text-blue-800',
};

const targetIcons = {
  USER: User,
  ASSET: Box,
  LOCATION: MapPin,
  CATEGORY: Tag,
};

export default function AuditLog() {
  const { isAdminOrOps } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterTarget, setFilterTarget] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAction) params.action = filterAction;
      if (filterTarget) params.target_type = filterTarget;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await getAuditLogs(params);
      setLogs(res.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterTarget, startDate, endDate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const clearDateFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  // Helper function for access check
  const canAccess = () => {
    if (typeof isAdminOrOps === 'function') return isAdminOrOps();
    return false;
  };

  if (!canAccess()) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500 mt-2">Only Admin and Operations Coordinator can view audit logs.</p>
        </div>
      </div>
    );
  }

  const formatDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return '-';
    return Object.entries(details)
      .filter(([_, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
      .join(', ');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <ClipboardList className="h-6 w-6" />
          Audit Log
        </h1>
        <p className="text-gray-500">Track all system activity and changes</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Action Type</Label>
              <Select value={filterAction || 'all'} onValueChange={(v) => setFilterAction(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-action">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="LOGIN">Login</SelectItem>
                  <SelectItem value="USER_CREATED">User Created</SelectItem>
                  <SelectItem value="USER_UPDATED">User Updated</SelectItem>
                  <SelectItem value="ASSET_CREATED">Asset Created</SelectItem>
                  <SelectItem value="ASSET_UPDATED">Asset Updated</SelectItem>
                  <SelectItem value="ASSET_TRANSFERRED">Asset Transferred</SelectItem>
                  <SelectItem value="ASSET_RECEIVED">Asset Received</SelectItem>
                  <SelectItem value="REMOVAL_REQUESTED">Removal Requested</SelectItem>
                  <SelectItem value="REMOVAL_APPROVED">Removal Approved</SelectItem>
                  <SelectItem value="EDIT_REQUESTED">Edit Requested</SelectItem>
                  <SelectItem value="EDIT_APPROVED">Edit Approved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Target Type</Label>
              <Select value={filterTarget || 'all'} onValueChange={(v) => setFilterTarget(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-target">
                  <SelectValue placeholder="All Targets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Targets</SelectItem>
                  <SelectItem value="USER">Users</SelectItem>
                  <SelectItem value="ASSET">Assets</SelectItem>
                  <SelectItem value="LOCATION">Locations</SelectItem>
                  <SelectItem value="CATEGORY">Categories</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Start Date
              </Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date"
              />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                End Date
              </Label>
              <div className="flex gap-1">
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="end-date"
                />
                {(startDate || endDate) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={clearDateFilters}
                    className="shrink-0"
                    title="Clear dates"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* Active filters display */}
          {(startDate || endDate) && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
              <span>Showing logs from:</span>
              {startDate && <Badge variant="outline">{startDate}</Badge>}
              {startDate && endDate && <span>to</span>}
              {endDate && <Badge variant="outline">{endDate}</Badge>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead className="hidden md:table-cell">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      No audit logs found
                      {(startDate || endDate) && ' for the selected date range'}
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => {
                    const TargetIcon = targetIcons[log.target_type] || Box;
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm text-gray-600 whitespace-nowrap">
                          {formatDateTime(log.created_at)}
                        </TableCell>
                        <TableCell className="font-medium">{log.user_name}</TableCell>
                        <TableCell>
                          <Badge className={actionColors[log.action] || 'bg-gray-100 text-gray-800'}>
                            {log.action.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <TargetIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm">{log.target_type}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-gray-500 max-w-xs truncate">
                          {formatDetails(log.details)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
